<template>
  <div class="input-frame-container">
    <FileDrop />
    <details open>
      <summary>Required Inputs for Analysis</summary>
      <div class="double-column">
        <div class="flex-item">
          <FileRowCad :key="cadFile" :file="cadFile" file-type="mainCad" />
          <Units @change-unit="updatePartNewPart($event)" />
          <AssemblyRipper />
        </div>
        <div class="flex-item">
          <EnableCadLessCalculation />
          <CADLessInput />
        </div>
      </div>
      <div class="double-column">
        <Quantity class="flex-item" @lot-size-changed="updatePartNewPart($event)" />
      </div>
      <div class="double-column">
        <CreatedDate class="pl-10" @created-date-changed="updatePartNewPart($event)" />
      </div>
    </details>

    <details open>
      <summary>Infos</summary>
      <!-- <CategoryInput required-categories @category="updatePartNewPart($event)" /> -->
      <CategoryInput @category="updatePartNewPart($event)" />
      <Description class="mt-16" v-if="!user.is_external" @info="updatePartNewPart($event)" />
    </details>

    <details open>
      <summary>Technical Drawing and Current Material</summary>
      <div>
        <div>
          <div v-for="drawing in technicalDrawings" :key="drawing">
            <FileRowAsset
              v-if="!user.is_external"
              :file="drawing"
              :class="[drawing.basename !== '' ? 'clickable' : '']"
              :style="previewFileName === drawing.basename && drawing.basename !== '' ? 'background-color:#ededed' : ''"
              @show-preview="showFile(drawing.basename)"
              @material-change="updatePartNewPart($event)"
            />
          </div>
          
          <!-- Drawing Analysis Section -->
          <div v-if="hasDrawingFile && !user.is_external" class="drawing-analysis-section mt-16">
            <div class="flex items-center gap-16">
              <SparkButton
                :disabled="isAnalyzingDrawing"
                @click="analyzeDrawing"
                class="bg-primary-500 text-white px-16 py-8 rounded-4"
              >
                <template v-if="isAnalyzingDrawing">
                  <SparkSpinner class="w-16 h-16 mr-8" />
                  Extracting Dimensions...
                </template>
                <template v-else>
                  Extract Dimensions
                </template>
              </SparkButton>
              
              <div v-if="drawingAnalysisError" class="text-red-500 text-12">
                {{ drawingAnalysisError }}
              </div>
            </div>
            
            <!-- Analysis Results Display -->
            <div v-if="drawingAnalysisResult" class="drawing-analysis-results mt-16 p-16 bg-gray-50 rounded-4">
              <h4 class="font-semibold text-14 mb-12 text-gray-800">Drawing Analysis Results</h4>
              
              <div class="results-grid">
                <!-- OCR Results -->
                <div v-if="drawingAnalysisResult.ocr_results" class="result-section">
                  <h5 class="font-medium text-12 mb-8 text-gray-700">Text Recognition (OCR)</h5>
                  <div class="bg-white p-12 rounded-4 border border-gray-200">
                    <div v-for="(text, index) in drawingAnalysisResult.ocr_results" :key="index" class="text-11 mb-4">
                      {{ text }}
                    </div>
                  </div>
                </div>
                
                <!-- Dimensions -->
                <div v-if="drawingAnalysisResult.dimensions" class="result-section">
                  <h5 class="font-medium text-12 mb-8 text-gray-700">Extracted Dimensions</h5>
                  <div class="bg-white p-12 rounded-4 border border-gray-200">
                    <div v-for="(dimension, index) in drawingAnalysisResult.dimensions" :key="index" class="text-11 mb-4">
                      {{ dimension.label }}: {{ dimension.value }} {{ dimension.unit }}
                    </div>
                  </div>
                </div>
                
                <!-- Materials -->
                <div v-if="drawingAnalysisResult.materials" class="result-section">
                  <h5 class="font-medium text-12 mb-8 text-gray-700">Detected Materials</h5>
                  <div class="bg-white p-12 rounded-4 border border-gray-200">
                    <div v-for="(material, index) in drawingAnalysisResult.materials" :key="index" class="text-11 mb-4">
                      {{ material.name }} ({{ material.confidence }}%)
                    </div>
                  </div>
                </div>
                
                <!-- General Information -->
                <div v-if="drawingAnalysisResult.general_info" class="result-section">
                  <h5 class="font-medium text-12 mb-8 text-gray-700">General Information</h5>
                  <div class="bg-white p-12 rounded-4 border border-gray-200">
                    <pre class="text-11 whitespace-pre-wrap">{{ JSON.stringify(drawingAnalysisResult.general_info, null, 2) }}</pre>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <CurrentMaterialSelection
            v-if="!user.is_external"
            class="w-1/2"
            @material-change="updatePartNewPart($event)"
          />
          
        </div>
      </div>
    </details>
    <div class="mb-100">
      <details open>
        <summary>Files</summary>
        <div v-for="asset in assets" :key="asset" class="margin-top-20">
          <FileRowAsset
            v-if="asset.type !== 'analysis' && !user.is_external"
            :file="asset"
            file-type="asset"
            :class="[asset.basename !== '' ? 'clickable' : '']"
            :style="previewFileName === asset.basename && asset.basename !== '' ? 'background-color:#ededed' : ''"
            @show-preview="showFile(asset.basename)"
          />
        </div>
      </details>
    </div>
    <FilePreviewModal
      v-if="assetAmount > 0"
      :show="showFileModal"
      :partid="part.part_id"
      :filename="previewFileName"
      @close="showFileModal = false"
    />
    <router-view />
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex';

import AssemblyRipper from './AssemblyRipper.vue';
import CADLessInput from './CADLessInput.vue';
import CategoryInput from './CategoryInput.vue';
import CreatedDate from './CreatedDate.vue';
import CurrentMaterialSelection from './CurrentMaterialSelection.vue';
import Description from './Description.vue';
import EnableCadLessCalculation from './EnableCadLessCalculation.vue';
import FileDrop from './FileDrop.vue';
import FilePreviewModal from './FilePreviewModal.vue';
import FileRowAsset from './FileRowAsset.vue';
import FileRowCad from './FileRowCad.vue';
import Quantity from './Quantity.vue';
import Units from './Units.vue';
import SparkButton from '@/components/SparkComponents/SparkButton.vue';
import SparkSpinner from '@/components/SparkComponents/SparkSpinner.vue';

export default {
  name: 'InputFrame',

  components: {
    FileDrop,
    FileRowCad,
    FileRowAsset,
    CategoryInput,
    Quantity,
    Units,
    FilePreviewModal,
    Description,
    AssemblyRipper,
    CurrentMaterialSelection,
    CADLessInput,
    EnableCadLessCalculation,
    CreatedDate,
    SparkButton,
    SparkSpinner,
  },

  data() {
    return {
      previewFileName: '',
      assetsAndDrawingsLoaded: false,
      showFileModal: false,
      isAnalyzingDrawing: false,
      drawingAnalysisResult: null,
      drawingAnalysisError: null,
    };
  },

  computed: {
    ...mapState(['part', 'assetTypes', 'user', 'assetAmount']),
    ...mapState('application', ['axiosInstance']),
    ...mapGetters(['processChainsAnalysisFinished', 'cadStatus', 'assetAmount']),

    cadFile() {
      let fileObject = {
        title: 'CAD File*',
        default: 'max. 250 MB (.3mf, .obj, .ply, .stl, .step)',
        type: 'main_cad',
        part_id: '',
      };
      if (this.part.basename) {
        fileObject.part_id = this.part.part_id;
        fileObject.basename = this.part.basename;
        fileObject.date = this.part.created_date;
      }
      return fileObject;
    },

    technicalDrawings() {
      let drawings = [];
      if (this.part.assets !== undefined) {
        for (const [key, value] of Object.entries(this.part.assets)) {
          if (value.asset_type == 'drawing') {
            drawings.push({
              title: this.getAssetType(value.asset_type),
              type: value.asset_type,
              basename: value.basename,
              date: value.updated.split(' ')[0],
              part_id: this.part.part_id,
              uid: key,
            });
            this.setToLoaded();
          }
        }
        let drawingsEmpty = drawings.length === 0;
        if (drawingsEmpty) {
          drawings.push({
            title: 'Technical Drawing',
            default: 'Upload to extract information',
            type: 'drawing',
            basename: '',
          });
        }
      }
      return drawings;
    },

    assets() {
      let assets = [];
      if (this.part.assets !== undefined) {
        for (const [key, value] of Object.entries(this.part.assets)) {
          if (value.asset_type != 'drawing') {
            assets.push({
              title: this.getAssetType(value.asset_type),
              type: value.asset_type,
              basename: value.basename,
              date: value.updated.split(' ')[0],
              part_id: this.part.part_id,
              uid: key,
            });
          }
          this.setToLoaded();
        }
        let assetsEmpty = assets.length === 0;
        if (assetsEmpty) {
          assets.push({
            title: 'Additional Files',
            default: 'max. 250 MB (jpg, png, pdf, ...)',
            basename: '',
            part_id: '',
          });
        }
      }
      return assets;
    },

    hasDrawingFile() {
      return this.technicalDrawings.some(drawing => drawing.basename !== '');
    },
  },

  watch: {
    assetsAndDrawingsLoaded() {
      if (Object.keys(this.technicalDrawings).length != 0) {
        this.previewFileName = this.technicalDrawings[0].basename;
      } else if (Object.keys(this.assets).length != 0) {
        this.previewFileName = this.assets[0].basename;
      }
    },

    popup(popup) {
      if (popup?.key == this.$options.name) {
        if (popup?.clicked == 'ok') {
          this.triggerPopup('');
        } else if (popup?.clicked == 'cancel' || popup?.clicked == 'close') {
          this.triggerPopup('');
        }
      }
    },
  },

  methods: {
    ...mapMutations([
      'updatePart',
      'updateComboCategories',
      'updateTextCategories',
      'updateUnsavedChanges',
      'triggerPopup',
    ]),

    setToLoaded() {
      this.assetsAndDrawingsLoaded = true;
    },

    getAssetType(type) {
      let obj = Object.values(this.assetTypes).find(value => value.value === type);
      if (obj === undefined) return 'Attachment';
      return obj.text;
    },

    updatePartNewPart(formData) {
      if (Object.prototype.hasOwnProperty.call(formData, 'combo_categories')) {
        this.updateComboCategories(formData['combo_categories']);
        // change data structure for backend
        for (const [key, value] of Object.entries(formData['combo_categories'])) {
          formData['combo_categories'][key] = value.uid;
        }
      } else if (Object.prototype.hasOwnProperty.call(formData, 'text_categories')) {
        // pass
      } else {
        this.updatePart(formData);
      }

      this.setOutdatedIfNeeded(formData);

      if (this.part.part_id != undefined && this.part.part_id != '' && this.part.part_id != 0) {
        this.savePart(formData);
      } else {
        // save unsaved changes in the store
        this.updateUnsavedChanges(formData);
      }
    },

    setOutdatedIfNeeded(formData) {
      if (
        Object.prototype.hasOwnProperty.call(formData, 'lot_size') ||
        Object.prototype.hasOwnProperty.call(formData, 'upload_file_units')
      ) {
        if (this.analysisFinished) {
          // this.saveProcessChainStat();
        }
      }
    },

    showFile(event) {
      this.previewFileName = event;
      this.showFileModal = true;
    },

    savePart(formData) {
      // This function will parse the current 'part' instance to the database.
      this.axiosInstance
        .put(`/api/v1/part/${this.part.part_id}/`, formData)
        .then(response => {
          this.updatePart(response.data);
        })
        .catch(error => {
          console.log(JSON.stringify(error));
        });
    },

    async analyzeDrawing() {
      if (!this.part.part_id || !this.hasDrawingFile) {
        this.drawingAnalysisError = 'No drawing file available for analysis';
        return;
      }

      this.isAnalyzingDrawing = true;
      this.drawingAnalysisError = null;
      this.drawingAnalysisResult = null;

      try {
        // Create a custom axios instance with no timeout for this long-running request
        const response = await this.axiosInstance.post(
          `/api/v1/drawing-analysis/${this.part.part_id}/`,
          {
            analysis_type: 'full',
            include_ocr: true,
            extract_dimensions: true,
          },
          {
            timeout: 0, // No timeout - wait indefinitely
            // Add headers to prevent browser from canceling
            headers: {
              'Keep-Alive': 'timeout=0, max=1000',
              'Connection': 'keep-alive'
            }
          }
        );

        this.drawingAnalysisResult = response.data.analysis_result;
        
        // Show success message
        this.triggerPopup({
          key: 'drawing-analysis-success',
          title: 'Drawing Analysis Complete',
          message: 'The drawing has been successfully analyzed. Check the results below.',
          type: 'success',
        });

        // Log successful analysis
        console.log('Drawing analysis completed successfully:', response.data);

      } catch (error) {
        console.error('Drawing analysis error:', error);
        
        // Handle different error types
        if (error.code === 'ECONNABORTED') {
          this.drawingAnalysisError = 'Request was cancelled - this can happen if you switch tabs during analysis. The analysis may still be running in the background.';
        } else if (error.response?.data?.error_message) {
          this.drawingAnalysisError = error.response.data.error_message;
        } else if (error.response?.status === 503) {
          this.drawingAnalysisError = 'Drawing analysis service is currently unavailable. Please try again later.';
        } else if (error.response?.status === 400) {
          this.drawingAnalysisError = 'Invalid drawing file or analysis parameters.';
        } else if (error.response?.status === 500) {
          // Show the actual analysis service error for debugging
          const analysisError = error.response?.data?.error_message || 
                               error.response?.data?.detail || 
                               'Internal analysis service error';
          this.drawingAnalysisError = `Analysis service error: ${analysisError}. This may be due to the drawing format or content - try with a different drawing file.`;
        // } else if (error.message?.includes('Network Error')) {
        //   this.drawingAnalysisError = 'Network connection lost. Please check your internet connection and try again.';
        } else {
          this.drawingAnalysisError = `Analysis failed: ${error.message || 'Unknown error'}. Please try again.`;
        }

        this.triggerPopup({
          key: 'drawing-analysis-error',
          title: 'Drawing Analysis Failed',
          message: this.drawingAnalysisError,
          type: 'error',
        });

      } finally {
        this.isAnalyzingDrawing = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
details {
  margin: 20px 0px 20px 10px;
  border: 1px solid var(--lighter-color);
  border-radius: 5px 5px 0px 0px;
}

summary {
  background-color: var(--lighter-color);
  padding: 10px;
  border-radius: 5px 5px 0px 0px;
}

.flex-item {
  width: 50%;
}

.column {
  display: flex;
  flex-direction: column;
}

.input-frame-container {
  display: flex;
  flex-direction: column;
  background-color: white;
  padding: 20px;
  border-radius: 3px;
}

.clickable {
  cursor: pointer;
}

.input-heading {
  margin-top: 10px;
  white-space: nowrap;
  font-size: var(--13px);
}

.double-column {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
}

.flex-row {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

.margin-top-20 {
  margin-top: 20px;
}

/* Drawing Analysis Specific Styles */
.drawing-analysis-section {
  border-top: 1px solid #e5e7eb;
  padding-top: 16px;
}

.drawing-analysis-results {
  max-height: 400px;
  overflow-y: auto;
}

.results-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.result-section {
  display: flex;
  flex-direction: column;
}

@media (max-width: 768px) {
  .results-grid {
    grid-template-columns: 1fr;
  }
}

/* Custom scrollbar for results */
.drawing-analysis-results::-webkit-scrollbar {
  width: 6px;
}

.drawing-analysis-results::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.drawing-analysis-results::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.drawing-analysis-results::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
